// ─────────────────────────────────────────────────────────────
// QnapCompatService — QNAP 공식 호환성 목록 조회 (v7.358)
//
// ⚠ 이 데이터는 QNAP 사이트에서 주기적으로 받아 온 [사본] 이다.
//   화면에 반드시 수집 시각과 원문 링크를 함께 노출해야 한다.
//   오래된 값으로 고객에게 안내하면 책임은 QNAP 이 아니라 우리에게 온다.
//
// 수집: n8n [QNAP 호환성 동기화] 워크플로우, 매주 화 03:00.
// ─────────────────────────────────────────────────────────────
import { supabase } from '../supabase';

// API 의 type 파라미터. DB qnap_category 와 같은 값이다.
//   v7.364: EXPANSION(11) 추가. QNAP 수집 대상은 아니고(is_active=false)
//   [사내 테스트] 에서만 쓰는 카테고리다.
//   v7.367: M2SSD(23) 추가 — 실측 확인. TBS-453DX 의 M.2 목록이 여기 있었다.
//     ⚠ SSD(3) 는 2.5" SATA 다. M.2 는 별개 type 이라 3 만 봐서는 영영 안 나온다.
export const QNAP_CAT = { HDD: 1, SSD: 3, M2SSD: 23, MEMORY: 33, EXPANSION: 11 };

export const QNAP_CAT_LABEL = {
  33: '메모리',
  1: 'HDD',
  3: 'SSD',
  23: 'M.2 SSD',
  11: '확장 카드',
};

// 수집 후 이 일수를 넘기면 화면에 [오래됨] 을 띄운다
const STALE_DAYS = 14;

// 모델명 정규화 — 재고 대조용.
//   ⚠ QNAP 쪽 데이터에는 눈에 안 보이는 문자가 섞여 있다 (QNAP-COMPAT.md §3).
//     nbsp / zero-width 계열은 일반 trim() 으로 안 지워진다. 안 걸러내면 조인이 조용히 실패한다.
//   ⚠ 하이픈·공백·대소문자도 무시한다. inventory.model_no 는 수기 입력이라 제각각이다.
//     'ST24000NT002' / 'st24000nt002' / 'ST-24000NT002' 가 모두 같은 물건이다.
const INVISIBLE = /[\u00a0\u200b\u200c\u200d\u2060\ufeff]/g;
export const qnapNorm = (v) =>
  String(v || '').replace(INVISIBLE, '').replace(/[\s-]/g, '').toUpperCase();

// 창고 코드 → 화면 표기. inventory.warehouse 는 'hq' / 'paju' 로 저장된다.
//   ⚠ 코드를 그대로 노출하면 [보유 hq 3] 처럼 나온다. 다른 화면은 전부 한글로 보여준다.
const WAREHOUSE_LABEL = { hq: '본사', paju: '파주' };
export const warehouseLabel = (w) => WAREHOUSE_LABEL[w] || w || '미지정';

export const QnapCompatService = {
  // 카탈로그 제품명 + 카테고리 → 호환 목록
  //   state: ok | none | not_synced | sync_error | unlinked | excluded
  //     none      = QNAP 이 정상 응답했고 목록이 비어 있다 (예: 메모리 온보드 모델)
  //     not_synced= 아직 수집한 적 없다
  //     unlinked  = 카탈로그 제품이 QNAP 모델과 연결되지 않았다
  //   ⚠ 셋 다 "행 0개"라 구분하지 않으면 직원이 헛수고한다.
  async forProduct(productName, categoryType) {
    if (!productName) return { state: 'unlinked', rows: [] };
    const { data, error } = await supabase.rpc('fn_qnap_compat_for_product', {
      p_product: productName,
      p_category: categoryType,
    });
    if (error) return { state: 'error', rows: [], error: error.message };

    const res = data || {};
    const rows = Array.isArray(res.rows) ? res.rows : [];
    const at = res.synced_at ? new Date(res.synced_at) : null;
    return {
      state: res.state || 'error',
      qnapModel: res.qnap_model || null,
      modelId: res.model_id || null,
      syncedAt: res.synced_at || null,
      stale: at ? (Date.now() - at.getTime()) / 86400000 > STALE_DAYS : false,
      rows,
    };
  },

  // v7.359: 카테고리 탭에 건수를 붙이려면 세 카테고리 수를 미리 알아야 한다.
  //   ⚠ 탭을 눌러 봐야 데이터 유무를 아는 건 나쁜 설계다. HDD 245건인지 0건인지
  //     누르기 전에 보여야 헛클릭이 없다.
  //   ⚠ head:true + count:'exact' 라 본문(행)을 하나도 안 받는다. 3회 왕복이지만
  //     전송량은 0에 가깝다. RPC 를 새로 만들면 SQL 마이그레이션이 또 필요해서 피했다.
  async countsByCategory(modelId) {
    if (!modelId) return null;
    //   v7.367: M.2 SSD 포함. 4회 왕복이지만 head:true 라 전송량은 여전히 0에 가깝다.
    const cats = [QNAP_CAT.MEMORY, QNAP_CAT.HDD, QNAP_CAT.SSD, QNAP_CAT.M2SSD];
    const out = {};
    await Promise.all(cats.map(async (c) => {
      const { count, error } = await supabase
        .from('qnap_compatibility')
        .select('*', { count: 'exact', head: true })
        .eq('model_id', modelId)
        .eq('category_type', c);
      out[c] = error ? null : (count || 0);
    }));
    return out;
  },

  // v7.360: 호환 목록에 [우리 재고] 를 붙인다.
  //
  //   ⚠ 조인 키가 까다롭다. QNAP 은 'ST24000NT002' / 'RAM-8GDR4D0-SO-3200' 처럼
  //     제조사 파트넘버를 쓰는데, inventory 에는 두 컬럼이 있다:
  //       product_name — 카탈로그 SKU (예: 'Seagate IronWolf Pro 24TB')  ← 표기가 다르다
  //       model_no     — 시리얼 단위 수기 모델번호 (예: 'ST24000NT002') ← QNAP 과 같은 체계
  //     둘 다 색인에 넣는다. model_no 가 비어 있는 오래된 행도 있기 때문이다.
  //
  //   ⚠ 재고는 어느 NAS 를 보든 똑같다 → 탭에 들어올 때 한 번만 받는다.
  //     카테고리를 바꿀 때마다 다시 받으면 의미 없는 왕복이 늘어난다.
  //   ⚠ status='in_stock' 만 받는다. 출고/불용까지 세면 "있다"고 해놓고 없는 상황이 된다.
  //     현재 보유 1,800건 규모라 3개 컬럼만 받으면 전송량이 작다.
  async stockIndex() {
    const { data, error } = await supabase
      .from('inventory')
      .select('model_no,product_name,warehouse')
      .eq('status', 'in_stock');
    if (error) return {};
    const idx = {};
    const bump = (key, w) => {
      if (!key) return;
      if (!idx[key]) idx[key] = { total: 0, byWarehouse: {} };
      idx[key].total += 1;
      idx[key].byWarehouse[w] = (idx[key].byWarehouse[w] || 0) + 1;
    };
    (data || []).forEach(r => {
      const w = warehouseLabel(r.warehouse);
      const mn = qnapNorm(r.model_no);
      const pn = qnapNorm(r.product_name);
      bump(mn, w);
      // ⚠ 같은 행을 두 번 세면 안 된다. model_no 와 product_name 이 정규화 후
      //   같아지는 경우(모델번호를 제품명에 그대로 쓴 경우)를 걸러낸다.
      if (pn && pn !== mn) bump(pn, w);
    });
    return idx;
  },

  // v7.361: 역방향 — 부품 모델명으로 [이 부품이 들어가는 NAS] 를 찾는다.
  //
  //   ⚠ migrations 02 의 fn_qnap_compat_by_drive() 를 그대로 쓸 수 없다:
  //       ・ 완전 일치만 된다 (upper(model_base) = key). 부분 검색이 안 된다
  //       ・ QNAP 모델명을 돌려준다. 우리는 카탈로그 product_name 이 필요하다
  //     새 RPC 를 만들면 SQL 마이그레이션이 또 필요해서 기존 테이블로 처리한다.
  //
  //   ⚠ 사용자 입력을 .or() 필터에 그대로 넣으면 안 된다.
  //     PostgREST 는 쉼표·괄호·마침표를 문법으로 쓰고 %/_ 는 LIKE 와일드카드다.
  //     실제 파트넘버는 영숫자·하이픈·별표 정도라 그 밖은 털어낸다.
  async nasForPart(q) {
    const safe = String(q || '').replace(/[^A-Za-z0-9 *-]/g, '').trim();
    if (safe.length < 3) return null;
    const { data, error } = await supabase
      .from('qnap_compatibility')
      .select('model_id,model_base,model_alt,brand,capacity_gb,category_type')
      .or(`model_base.ilike.%${safe}%,model_alt.ilike.%${safe}%`)
      .limit(4000);
    if (error) return null;
    const modelIds = new Set();
    const parts = new Map();     // 어떤 부품이 걸렸는지 (여러 개일 수 있다)
    (data || []).forEach(r => {
      modelIds.add(r.model_id);
      const k = r.model_base;
      if (!parts.has(k)) parts.set(k, { model: k, brand: r.brand, capacity_gb: r.capacity_gb, categoryType: r.category_type });
    });
    if (!modelIds.size) return { parts: [], productNames: new Set() };

    // QNAP 모델 → 카탈로그 제품명.
    //   ⚠ excluded 는 뺀다 (수리 자산 등). unlinked 는 애초에 model_id 가 없다.
    const { data: links } = await supabase
      .from('qnap_product_link')
      .select('product_name,model_id,status')
      .in('model_id', [...modelIds]);
    const productNames = new Set();
    (links || []).forEach(l => { if (l.status !== 'excluded') productNames.add(l.product_name); });
    return { parts: [...parts.values()].slice(0, 8), productNames };
  },

  // 폴더 격자에서 [호환성 있음] 표시를 하려면 한 번에 받아 둔다.
  //   ⚠ 제품마다 조회하면 82회 왕복이 된다.
  async linkMap() {
    const { data, error } = await supabase
      .from('qnap_product_link')
      .select('product_name,model_id,status');
    if (error) return {};
    const map = {};
    (data || []).forEach(r => { map[r.product_name] = r; });
    return map;
  },

  // 원문 링크 — 화면 하단 출처 표기에 쓴다
  //   ⚠ 원문 페이지의 category 값은 API 의 type 과 체계가 다르다.
  //     M.2 SSD 는 페이지상 SSD 아래 서브탭이라 SSD 페이지(1)로 보낸다 —
  //     들어가서 [M.2 SSD] 탭을 누르면 같은 목록이 나온다.
  sourceUrl(modelId, categoryType) {
    const cat = categoryType === QNAP_CAT.HDD ? 0
              : (categoryType === QNAP_CAT.SSD || categoryType === QNAP_CAT.M2SSD) ? 1 : 22;
    return `https://www.qnap.com/ko-kr/compatibility/?model=${modelId}&category=${cat}`;
  },

  // 용량 표기 — HDD/SSD 는 GB 로 들어오지만 TB 로 보는 게 자연스럽다
  fmtCapacity(gb, categoryType) {
    if (gb == null) return '';
    if (categoryType === QNAP_CAT.MEMORY) return `${gb}GB`;
    return gb >= 1000 ? `${+(gb / 1000).toFixed(gb % 1000 ? 1 : 0)}TB` : `${gb}GB`;
  },
};
